import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import type { ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { api, isUnauthorized, setUnauthorizedHandler } from '@/lib/api'
import { qk } from '@/lib/queryClient'
import type { Features, MeResponse, ServerLinks, User } from '@/lib/types'

interface AuthContextValue {
  user: User | null
  features: Features | null
  /** Public URLs for Jellyfin and Seerr, for the links on the board. */
  server: ServerLinks | null
  /** True only during the first `/api/auth/me` round trip. */
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  /** Set when `me` failed for a reason other than "not signed in". */
  error: unknown
  login: (input: { username: string; password: string }) => Promise<User>
  isLoggingIn: boolean
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

const PUBLIC_PREFIXES = ['/login', '/invite/']
const isPublicPath = (path: string): boolean =>
  PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix))

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()

  // Read inside the 401 handler without re-registering it on every navigation.
  const locationRef = useRef(location)
  locationRef.current = location
  const evictingRef = useRef(false)

  const meQuery = useQuery<MeResponse | null>({
    queryKey: qk.me,
    queryFn: async ({ signal }) => {
      try {
        return await api.auth.me(signal)
      } catch (error) {
        // Anonymous is a valid answer, not a failure.
        if (isUnauthorized(error)) return null
        throw error
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
  })

  useEffect(() => {
    return setUnauthorizedHandler(() => {
      // A burst of in-flight requests can 401 together; handle the first only,
      // otherwise the cache-clear/refetch cycle can chase its own tail.
      if (evictingRef.current) return
      evictingRef.current = true
      window.setTimeout(() => {
        evictingRef.current = false
      }, 1500)

      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' })
      queryClient.setQueryData<MeResponse | null>(qk.me, null)

      const { pathname, search } = locationRef.current
      if (isPublicPath(pathname)) return
      navigate('/login', { replace: true, state: { from: `${pathname}${search}` } })
    })
  }, [navigate, queryClient])

  const loginMutation = useMutation({
    mutationFn: api.auth.login,
    onSuccess: async () => {
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' })
      await queryClient.invalidateQueries({ queryKey: qk.me })
    },
  })

  const login = useCallback(
    async (input: { username: string; password: string }) => {
      const result = await loginMutation.mutateAsync(input)
      return result.user
    },
    [loginMutation],
  )

  const logout = useCallback(async () => {
    try {
      await api.auth.logout()
    } finally {
      queryClient.setQueryData<MeResponse | null>(qk.me, null)
      queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'auth' })
      navigate('/login', { replace: true })
    }
  }, [navigate, queryClient])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: qk.me })
  }, [queryClient])

  const value = useMemo<AuthContextValue>(() => {
    const user = meQuery.data?.user ?? null
    return {
      user,
      features: meQuery.data?.features ?? null,
      server: meQuery.data?.server ?? null,
      isLoading: meQuery.isPending,
      isAuthenticated: user !== null,
      isAdmin: user?.isAdmin === true,
      error: meQuery.error,
      login,
      isLoggingIn: loginMutation.isPending,
      logout,
      refresh,
    }
  }, [
    meQuery.data,
    meQuery.isPending,
    meQuery.error,
    login,
    loginMutation.isPending,
    logout,
    refresh,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
